// src/utils/permissoes.js
import { PERMISSOES_DEFAULT } from "../config/permissoesDefault";
import { NIVEIS } from "../config/niveis";

// BYPASS TEMPORÁRIO: libera tudo para todos (UI + rotas).
// ✅ Mantemos DESLIGADO para as permissões funcionarem de verdade.
const PERMS_BYPASS = false;

// Áreas (domínios) do sistema: permitem ativar módulos (Financeiro)
// sem misturar regras com Manutenção.
export const AREAS = {
  MANUTENCAO: "MANUTENCAO",
  FINANCEIRO: "FINANCEIRO",
};

function normalizarChaveArea(valor) {
  if (valor == null) return "";
  const s = String(valor)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "") // remove acentos
    .trim()
    .toUpperCase();
  return s;
}


function normalizarAreasDoUsuario(usuario) {
  // 1) Se vier como objeto { MANUTENCAO:true, ... } (ou chaves com acento)
  if (usuario?.areas && typeof usuario.areas === "object" && !Array.isArray(usuario.areas)) {
    const entries = Object.entries(usuario.areas || {});
    const map = {};
    for (const [k, v] of entries) {
      map[normalizarChaveArea(k)] = !!v;
    }
    return {
      [AREAS.MANUTENCAO]: !!map[AREAS.MANUTENCAO] || !!map["MANUTENCAO"],
      [AREAS.FINANCEIRO]: !!map[AREAS.FINANCEIRO] || !!map["FINANCEIRO"] || !!map["FERRAMENTARIA"],
    };
  }

  // 2) Se vier como array ["MANUTENCAO", ...] (aceita acentos/variações)
  if (Array.isArray(usuario?.areas)) {
    const set = new Set(usuario.areas.map((a) => normalizarChaveArea(a)));
    return {
      [AREAS.MANUTENCAO]: set.has(AREAS.MANUTENCAO),
      [AREAS.FINANCEIRO]: set.has(AREAS.FINANCEIRO) || set.has("FERRAMENTARIA"),
    };
  }

  // 3) Se não existir, define um padrão seguro:
  // - Gestor/Admin: libera todas as áreas (facilita implantação)
  // - Demais: somente Manutenção
  const n = nivelUsuario(usuario);
  const tudo = n >= NIVEIS.ADMIN;
  return {
    [AREAS.MANUTENCAO]: true,
    [AREAS.FINANCEIRO]: !!tudo,
  };
}

export function temArea(usuario, area) {
  if (PERMS_BYPASS) return true;
  const areas = normalizarAreasDoUsuario(usuario);
  return !!areas?.[area];
}

// Converte o que vier do usuário (string, número, perfil...) para nível numérico 0-6
export function nivelUsuario(usuario) {
  if (!usuario) return -1;

  // 1) tenta pegar do perfil ou direto no objeto
  const bruto =
    usuario?.perfil?.nivel ??
    usuario?.nivel ??
    null;

  if (bruto == null) return -1;

  // Se já for número, beleza
  if (typeof bruto === "number") return bruto;

  // Se for string, pode ser "6", "GESTOR", etc.
  if (typeof bruto === "string") {
    const s = bruto.trim().toUpperCase();

    // Se for um número em string
    const num = Number(s);
    if (!Number.isNaN(num)) return num;

    // Se for texto, mapeia
    const mapa = {
      GESTOR: NIVEIS.GESTOR,
      ADMIN: NIVEIS.ADMIN,
      ADMINISTRADOR: NIVEIS.ADMIN,
      TECNICO: NIVEIS.TECNICO,
      "TÉCNICO": NIVEIS.TECNICO,
      TECNICO_SENIOR: NIVEIS.TECNICO_SENIOR,
      "TÉCNICO SÊNIOR": NIVEIS.TECNICO_SENIOR,
      CLIENTE: NIVEIS.CLIENTE,
      KIOSK: NIVEIS.KIOSK,
      DASHBOARD: NIVEIS.DASHBOARD,
    };

    return mapa[s] ?? -1;
  }

  // Qualquer coisa estranha → sem nível
  return -1;
}

// Retorna true se o usuário tiver pelo menos o nível mínimo
export function temNivelMinimo(usuario, minimo) {
  if (PERMS_BYPASS) return true;
  const nUser = nivelUsuario(usuario);
  if (nUser < 0) return false;
  return nUser >= minimo;
}

// Usa a tabela PERMISSOES_DEFAULT (cadastros, osCorretivas, preventivas, etc.)
export function pode(usuario, modulo, recurso, acao, opcoes = {}) {
  if (PERMS_BYPASS) return true;
  const nUser = nivelUsuario(usuario);
  if (nUser < 0) return false;

  const permissoesAtivas = opcoes?.permissoesAtivas || usuario?.permissoesAtivas || PERMISSOES_DEFAULT;

  // ✅ Fail-safe: regra inexistente = NEGA acesso
  // Isso evita "furo" quando uma tela/ação nova ainda não foi cadastrada na matriz.
  const cfgModulo = permissoesAtivas[modulo];
  if (!cfgModulo) return false;

  const cfgRecurso = cfgModulo[recurso];
  if (!cfgRecurso) return false;

  const regra = cfgRecurso[acao];
  if (!regra || regra.nivelMinimo == null) return false;

  const baseAllow = nUser >= regra.nivelMinimo;

  // Se existir mapa de escopo por nível, ele pode:
  // - conceder acesso ("todas" | "proprias") mesmo abaixo do nível mínimo
  // - negar acesso via exceção ("excecao") mesmo acima do nível mínimo
  const mapa = regra?.escopo;
  if (mapa && typeof mapa === "object") {
    const ov = (mapa?.[nUser] ?? mapa?.[String(nUser)] ?? "");
    const v = String(ov).toLowerCase();
    if (v === "excecao" || v === "exceção") return false;
    if (v === "todas" || v === "proprias" || v === "próprias") return true;
  }

  return baseAllow;
}

// Retorna o escopo efetivo ("proprias"|"todas") para ações que usam regra.escopo
export function escopoPermissao(usuario, modulo, recurso, acao, opcoes = {}) {
  const nUser = nivelUsuario(usuario);
  if (nUser < 0) return "proprias";

  const permissoesAtivas = opcoes?.permissoesAtivas || usuario?.permissoesAtivas || PERMISSOES_DEFAULT;
  const regra = permissoesAtivas?.[modulo]?.[recurso]?.[acao] || null;
  const nivelMinimo = regra?.nivelMinimo;
  const baseAllow = (nivelMinimo != null) ? (nUser >= nivelMinimo) : false;
  const mapa = regra?.escopo;
  if (!mapa || typeof mapa !== "object") {
    return nUser >= NIVEIS.ADMIN ? "todas" : "proprias";
  }
  const ov = mapa?.[nUser] || mapa?.[String(nUser)] || "";
  const v = String(ov).toLowerCase();
  if (v === "excecao" || v === "exceção") {
    // Mantemos retorno compatível ("proprias"|"todas") para não quebrar telas.
    // A negação efetiva deve ser feita por `pode()`.
    return "proprias";
  }
  // ✅ Regra visual: 
  // - Check verde (contemplado pelo nível mínimo) = "todas"
  // - P amarelo = "proprias"
  // - X vermelho = exceção (nega)
  if (v === "todas") return "todas";
  if (v === "proprias" || v === "próprias") return "proprias";

  // Sem override explícito:
  // - Se contemplado pelo nível mínimo → "todas"
  // - Caso contrário → "proprias" (compatibilidade)
  return baseAllow ? "todas" : "proprias";
}
